import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin } from 'lucide-react';
import { fetchContracts } from '../actions/contracts';
import { getMachineCoordinates } from '../utils/machines';
import '../styles/DashboardView.css';

// Fix default leaflet marker icons broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Adjusts map viewport to fit all markers whenever they change
const FitBounds = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) return;
    if (markers.length === 1) {
      map.setView([markers[0].coords.lat, markers[0].coords.lng], 12);
      return;
    }
    const bounds = L.latLngBounds(markers.map(m => [m.coords.lat, m.coords.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [markers, map]);
  return null;
};

const VERTICALS = [
  { value: '', label: 'Todas las verticales' },
  { value: 'Themepark', label: 'Waterpark / Themepark' },
  { value: 'Ski', label: 'Ski' },
  { value: 'FEC', label: 'FEC' },
  { value: 'Museum', label: 'Museum' },
  { value: 'Stadium', label: 'Stadium' },
  { value: 'Zoo', label: 'Aquarium / Zoo' },
  { value: 'Hotel', label: 'Hotel' },
  { value: 'Gym', label: 'Gym' },
];

const LocalizacionView = ({ contracts, loading, role, fetchContracts }) => {
  const isAdmin = role === 'ADMIN';
  const [verticalFilter, setVerticalFilter] = useState('');

  useEffect(() => {
    if (!contracts) {
      fetchContracts();
    }
  }, [contracts, fetchContracts]);

  const markers = useMemo(() => {
    if (!contracts) return [];

    return contracts.reduce((acc, contract) => {
      if (!contract.Location) return acc;
      const coords = getMachineCoordinates(contract.Location);
      if (!coords) return acc;

      const vertical = contract.Customer?.Vertical || '';
      if (isAdmin && verticalFilter && vertical !== verticalFilter) return acc;

      acc.push({ contract, coords });
      return acc;
    }, []);
  }, [contracts, verticalFilter, isAdmin]);

  const mapCenter = markers.length > 0
    ? [markers[0].coords.lat, markers[0].coords.lng]
    : [40.416775, -3.703790]; // default: Madrid

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
      {/* Header */}
      <div className="dashboard-header" style={{ borderRadius: 0, marginBottom: 0 }}>
        <div className="dashboard-title-section">
          <div>
            <h1 className="dashboard-title">
              <i className="fas fa-map-marked-alt"></i> Localización
            </h1>
            <div className="small text-muted mt-1">
              {loading
                ? 'Cargando contratos...'
                : `${markers.length} máquina${markers.length !== 1 ? 's' : ''} en el mapa`}
            </div>
          </div>
        </div>

        {/* Vertical filter — ADMIN only */}
        {isAdmin && (
          <div className="dashboard-actions">
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', marginBottom: 0 }}>
              Vertical
            </label>
            <select
              value={verticalFilter}
              onChange={e => setVerticalFilter(e.target.value)}
              className="dashboard-filter-input"
              style={{ minWidth: 180 }}
            >
              {VERTICALS.map(v => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
            {verticalFilter && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 12px',
                background: '#001d3a',
                color: '#fff',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}>
                <MapPin size={13} />
                {markers.length} máquina{markers.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.7)',
          }}>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Cargando mapa...</div>
          </div>
        )}

        {!loading && (
          <MapContainer
            center={mapCenter}
            zoom={5}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds markers={markers} />

            {markers.map(({ contract, coords }) => (
              <Marker
                key={contract._id}
                position={[coords.lat, coords.lng]}
              >
                <Popup minWidth={220}>
                  <div style={{ fontFamily: 'inherit' }}>
                    {/* Title row */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      marginBottom: 10, paddingBottom: 8,
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        background: 'linear-gradient(135deg, #001d3a, #0b6fd4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <MapPin size={14} color="#fff" />
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', lineHeight: 1.2 }}>
                          {contract.Machine?.Serial || contract._id}
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                          {contract.Machine?.Name || 'Kiosco'}
                        </div>
                      </div>
                    </div>

                    {/* Fields */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {contract.Customer?.Fullname && (
                        <div style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                          <span style={{ color: '#6b7280', minWidth: 72 }}>Cliente</span>
                          <span style={{ fontWeight: 600, color: '#111827' }}>{contract.Customer.Fullname}</span>
                        </div>
                      )}
                      {contract.Customer?.Vertical && (
                        <div style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                          <span style={{ color: '#6b7280', minWidth: 72 }}>Vertical</span>
                          <span style={{ fontWeight: 600, color: '#111827' }}>{contract.Customer.Vertical}</span>
                        </div>
                      )}
                      {contract.Zone !== undefined && (
                        <div style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                          <span style={{ color: '#6b7280', minWidth: 72 }}>Zona</span>
                          <span style={{ fontWeight: 600, color: '#111827' }}>{contract.Zone}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                        <span style={{ color: '#6b7280', minWidth: 72 }}>Estado</span>
                        <span style={{
                          fontWeight: 600,
                          color: contract.Status === 'VALID' ? '#16a34a' : '#d97706'
                        }}>
                          {contract.Status}
                        </span>
                      </div>
                      {contract.Location && (
                        <div style={{ display: 'flex', gap: 6, fontSize: 11, marginTop: 2 }}>
                          <span style={{ color: '#9ca3af', minWidth: 72 }}>Coords</span>
                          <span style={{ color: '#9ca3af' }}>{contract.Location}</span>
                        </div>
                      )}
                    </div>

                    {/* Link to contract */}
                    <div style={{ marginTop: 10 }}>
                      <Link
                        to={`/machines/${contract.Machine?._id || contract.Machine}`}
                        style={{
                          display: 'inline-block',
                          padding: '5px 12px',
                          background: '#001d3a',
                          color: '#fff',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          textDecoration: 'none',
                        }}
                      >
                        Ver máquina →
                      </Link>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  contracts: state.contracts.contracts,
  loading: state.contracts.loading,
  role: state.auth.role,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ fetchContracts }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(LocalizacionView);
